import { useState, useEffect } from "react";
import { Star, BadgeCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const Stars = ({ value, onChange, size = 16 }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type={onChange ? "button" : undefined} disabled={!onChange}
        data-testid={onChange ? `star-input-${n}` : undefined}
        onClick={onChange ? () => onChange(n) : undefined}
        className={n <= value ? "text-[#B8860B]" : "text-black/20"}>
        <Star size={size} fill={n <= value ? "currentColor" : "none"} />
      </button>
    ))}
  </div>
);

export const Reviews = ({ slug, onAggregate }) => {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", rating: 5, title: "", comment: "" });
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { api.get(`/products/${slug}/reviews`).then(({ data }) => setReviews(data)).catch(() => {}); }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/products/${slug}/reviews`, form);
      setReviews([data, ...reviews]);
      setShowForm(false);
      setForm({ name: "", rating: 5, title: "", comment: "" });
      toast.success("Thanks for your review!");
      onAggregate?.();
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        toast.error("Please log in to submit a review.", {
          action: { label: "Log In", onClick: () => { window.location.href = "/login"; } },
        });
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit your review. Please try again.");
      }
    }
  };

  return (
    <div className="mt-14" data-testid="reviews-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h2 className="font-heading text-2xl tracking-tight uppercase font-bold">Customer Reviews</h2>
          <p className="text-sm text-black/50 mt-1">{reviews.length === 0 ? "No reviews yet — be the first." : `${reviews.length} review${reviews.length > 1 ? "s" : ""}`}</p>
        </div>
        <button data-testid="write-review-btn" onClick={() => setShowForm(!showForm)}
          className="bg-[#121212] text-white px-6 py-3 text-xs font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150">
          Write a Review
        </button>
      </div>
      {showForm && (
        <form onSubmit={submit} data-testid="review-form" className="border border-black/10 bg-[#F4F3F0]/60 p-6 mb-8 max-w-xl space-y-4">
          <div><p className="text-[10px] font-bold uppercase tracking-widest text-black/50 mb-1">Your Rating</p>
            <Stars value={form.rating} onChange={(rating) => setForm({ ...form, rating })} size={22} /></div>
          <input data-testid="review-name-input" required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full border border-black/20 px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4A5D23]" />
          <input data-testid="review-title-input" placeholder="Review title (optional)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full border border-black/20 px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4A5D23]" />
          <textarea data-testid="review-comment-input" required rows={4} placeholder="How did this gear perform?" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })}
            className="w-full border border-black/20 px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4A5D23]" />
          <button data-testid="review-submit-btn" type="submit" className="bg-[#4A5D23] text-white px-8 py-3.5 text-xs font-bold uppercase tracking-widest hover:bg-[#3D4D1D] transition-colors duration-150">Submit Review</button>
        </form>
      )}
      <div className="space-y-4 max-w-3xl">
        {reviews.map((r) => (
          <div key={r.review_id} data-testid={`review-${r.review_id}`} className="border border-black/10 p-5 bg-white">
            <div className="flex items-center gap-3 flex-wrap">
              <Stars value={r.rating} size={13} />
              <span className="text-sm font-bold">{r.name}</span>
              {r.verified_buyer && <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#2E8B57]"><BadgeCheck size={13} /> Verified Buyer</span>}
              <span className="text-xs text-black/40 ml-auto">{new Date(r.created_at).toLocaleDateString()}</span>
            </div>
            {r.title && <p className="font-heading font-bold text-sm uppercase tracking-tight mt-2">{r.title}</p>}
            <p className="text-sm text-black/70 mt-1">{r.comment}</p>
          </div>
        ))}
      </div>
    </div>
  );
};
